/**
 * Contact Settings - использует переиспользуемые компоненты настроек
 */
import React from 'react';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import {
  SettingsGroup,
  TextSettings,
  SelectFieldSettings,
  RangeFieldSettings,
} from '../components/settings';
import { CONTACT_ICONS, SOCIAL_ICONS } from '../components/settings/iconsList';

// Хелпер для работы с текстовыми данными
const getTextData = (value) => {
  if (typeof value === 'object' && value !== null) return value;
  return { text: value || '' };
};

export default function ContactSettings({ data, onChange }) {
  const updateData = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const updateContact = (index, field, value) => {
    const newContacts = [...(data?.contacts || [])];
    newContacts[index] = { ...newContacts[index], [field]: value };
    updateData('contacts', newContacts);
  };

  const addContact = () => {
    const newContacts = [
      ...(data?.contacts || []),
      {
        id: Date.now().toString(),
        icon: 'Phone',
        label: 'Контакт',
        value: '',
        link: ''
      }
    ];
    updateData('contacts', newContacts);
  };

  const removeContact = (index) => {
    const newContacts = (data?.contacts || []).filter((_, i) => i !== index);
    updateData('contacts', newContacts);
  };

  const updateSocial = (index, field, value) => {
    const newSocials = [...(data?.socials || [])];
    newSocials[index] = { ...newSocials[index], [field]: value };
    updateData('socials', newSocials);
  };

  const addSocial = () => {
    const newSocials = [
      ...(data?.socials || []),
      {
        id: Date.now().toString(),
        icon: 'Link',
        link: '#'
      }
    ];
    updateData('socials', newSocials);
  };

  const removeSocial = (index) => {
    const newSocials = (data?.socials || []).filter((_, i) => i !== index);
    updateData('socials', newSocials);
  };

  return (
    <div className="space-y-6">
      {/* Контент */}
      <SettingsGroup title="Контент">
        <TextSettings
          label="Заголовок"
          value={getTextData(data?.title)}
          onChange={(value) => updateData('title', value)}
          placeholder="Свяжитесь с нами"
          defaults={{ size: '3xl', weight: 'bold', color: 'foreground' }}
        />
        <TextSettings
          label="Подзаголовок"
          value={getTextData(data?.subtitle)}
          onChange={(value) => updateData('subtitle', value)}
          placeholder="Мы рады помочь"
          defaults={{ size: 'lg', weight: 'normal', color: 'text-secondary' }}
        />
      </SettingsGroup>

      {/* Оформление */}
      <SettingsGroup title="Оформление">
        <SelectFieldSettings
          label="Стиль"
          value={data?.style || 'split'}
          onChange={(value) => updateData('style', value)}
          mode="grid"
          columns={3}
          options={[
            { value: 'split', label: 'Разделённый' },
            { value: 'centered', label: 'По центру' },
            { value: 'minimal', label: 'Минимал' },
          ]}
        />

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={data?.showForm !== false}
            onChange={(e) => updateData('showForm', e.target.checked)}
            className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
          />
          <span className="text-sm">Показать форму</span>
        </label>

        {data?.showForm !== false && (
          <TextSettings
            label="Заголовок формы"
            value={getTextData(data?.formTitle)}
            onChange={(value) => updateData('formTitle', value)}
            placeholder="Напишите нам"
            defaults={{ size: 'xl', weight: 'semibold', color: 'foreground' }}
          />
        )}

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={data?.showMap === true}
            onChange={(e) => updateData('showMap', e.target.checked)}
            className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
          />
          <span className="text-sm">Показать карту</span>
        </label>

        {data?.showMap && (
          <div className="space-y-3 pl-4 border-l-2 border-primary/20">
            <div>
              <label className="block text-sm font-medium mb-1">Координаты</label>
              <Input
                value={data?.mapCoords || ''}
                onChange={(e) => updateData('mapCoords', e.target.value)}
                placeholder="55.751244,37.618423"
              />
              <p className="text-xs text-text-secondary mt-1">
                Широта,Долгота
              </p>
            </div>

            <RangeFieldSettings
              label="Масштаб"
              value={data?.mapZoom || 15}
              onChange={(value) => updateData('mapZoom', value)}
              min={10}
              max={18}
            />

            <RangeFieldSettings
              label="Высота"
              value={data?.mapHeight || 300}
              onChange={(value) => updateData('mapHeight', value)}
              min={200}
              max={500}
              step={50}
              unit="px"
            />
          </div>
        )}
      </SettingsGroup>

      {/* Контакты */}
      <SettingsGroup
        title={`Контакты (${(data?.contacts || []).length})`}
        rightAction={
          <button
            onClick={addContact}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
          >
            <Icon name="Plus" size={12} />
            <span>Добавить</span>
          </button>
        }
      >
        <div className="space-y-3">
          {(data?.contacts || []).map((contact, index) => (
            <div key={contact.id || index} className="p-3 bg-muted rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-1">
                  {CONTACT_ICONS.map((icon) => (
                    <button
                      key={icon}
                      onClick={() => updateContact(index, 'icon', icon)}
                      className={`p-1.5 rounded transition-colors ${
                        contact.icon === icon
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background hover:bg-primary/10'
                      }`}
                    >
                      <Icon name={icon} size={14} />
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => removeContact(index)}
                  className="p-1 hover:bg-error/10 hover:text-error rounded"
                >
                  <Icon name="Trash2" size={14} />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={contact.label || ''}
                  onChange={(e) => updateContact(index, 'label', e.target.value)}
                  placeholder="Метка"
                />
                <Input
                  value={contact.value || ''}
                  onChange={(e) => updateContact(index, 'value', e.target.value)}
                  placeholder="Значение"
                />
              </div>

              <Input
                value={contact.link || ''}
                onChange={(e) => updateContact(index, 'link', e.target.value)}
                placeholder="Ссылка (опционально)"
              />
            </div>
          ))}
        </div>
      </SettingsGroup>

      {/* Соцсети */}
      <SettingsGroup
        title={`Соцсети (${(data?.socials || []).length})`}
        rightAction={
          <button
            onClick={addSocial}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
          >
            <Icon name="Plus" size={12} />
            <span>Добавить</span>
          </button>
        }
      >
        <div className="space-y-3">
          {(data?.socials || []).map((social, index) => (
            <div key={social.id || index} className="p-3 bg-muted rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-1">
                  {SOCIAL_ICONS.map((icon) => (
                    <button
                      key={icon}
                      onClick={() => updateSocial(index, 'icon', icon)}
                      className={`p-1.5 rounded transition-colors ${
                        social.icon === icon
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background hover:bg-primary/10'
                      }`}
                    >
                      <Icon name={icon} size={14} />
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => removeSocial(index)}
                  className="p-1 hover:bg-error/10 hover:text-error rounded"
                >
                  <Icon name="Trash2" size={14} />
                </button>
              </div>

              <Input
                value={social.link || ''}
                onChange={(e) => updateSocial(index, 'link', e.target.value)}
                placeholder="Ссылка"
              />
            </div>
          ))}
        </div>
      </SettingsGroup>
    </div>
  );
}
